// Plan math shared by the landing pages and the checkout. Prices come from /items in a base unit and are
// converted with the user's currency rate; savings are ratios, so they work even without a currency.

export const PLAN_MONTHS = { bronze: 1, silver: 3, gold: 6, platinum: 12 } as const;
export type Plan = keyof typeof PLAN_MONTHS;
export const PLAN_ORDER: Plan[] = ["bronze", "silver", "gold", "platinum"];

export type Currency = { code: string; symbol: string; name?: string; rate: number };
export type Item = { _id: string; id?: string; name: string; plan?: Plan | string; price: number; payment?: { gateway?: string } };
export type PlanView = {
  id: string;
  plan: Plan; // checkout mode can also carry an item without a known plan (months is then null)
  name: string;
  months: number | null;
  total: number | null; // in the user's currency, null when the currency is unknown (logged-out visitor)
  saving: number; // 0..1, compared with paying monthly
  isBest: boolean;
  item: Item;
};

export const isPlan = (value: unknown): value is Plan => typeof value === "string" && value in PLAN_MONTHS;

export function convert(price: number, currency: Currency) {
  const amount = price * currency.rate;
  return currency.code === "IRT" ? Math.round(amount) : Math.round(amount * 100) / 100;
}

export function formatNumber(value: number, fraction = 0) {
  return new Intl.NumberFormat("fa-IR", { maximumFractionDigits: fraction }).format(value);
}

export function formatMoney(amount: number, currency: Currency) {
  return `${formatNumber(amount, currency.code === "IRT" ? 0 : 2)} ${currency.symbol}`;
}

export function formatPercent(ratio: number) {
  return `${formatNumber(Math.round(ratio * 100))}٪`;
}

// Landing: one item per plan (prefers the given gateway). Checkout (`each`): every item it is given (already one gateway),
// so a second item for a plan or an item without a known plan stays on the list. Both run cheapest to most expensive.
export function buildPlans(items: Item[] | undefined, currency: Currency | null | undefined, gateway = "offline", each = false): PlanView[] {
  const byPlan = new Map<Plan, Item>();
  for (const item of items ?? []) {
    if (!isPlan(item.plan)) continue;
    const current = byPlan.get(item.plan);
    if (!current || (current.payment?.gateway !== gateway && item.payment?.gateway === gateway)) byPlan.set(item.plan, item);
  }
  const monthlyBase = byPlan.get("bronze")?.price ?? null;
  const picked = each ? (items ?? []) : PLAN_ORDER.filter((plan) => byPlan.has(plan)).map((plan) => byPlan.get(plan)!);
  const views = picked.map((item): PlanView => {
    const months = isPlan(item.plan) ? PLAN_MONTHS[item.plan] : null;
    const total = currency ? convert(item.price, currency) : null;
    return {
      id: item._id,
      plan: item.plan as Plan,
      name: item.name,
      months,
      total,
      saving: monthlyBase && months ? Math.max(0, 1 - item.price / months / monthlyBase) : 0,
      isBest: item === byPlan.get("platinum"),
      item,
    };
  });
  return views.sort((a, b) => a.item.price - b.item.price);
}

// Self-check, runs once in dev: 12 months at 7.9 vs 1 month at 1.29 saves ~49%.
if (import.meta.env?.DEV) {
  const p = buildPlans(
    [
      { _id: "1", name: "1", plan: "bronze", price: 1.29, payment: { gateway: "offline" } },
      { _id: "2", name: "12", plan: "platinum", price: 7.9, payment: { gateway: "zify" } },
      { _id: "3", name: "12", plan: "platinum", price: 7.9, payment: { gateway: "offline" } },
    ],
    { code: "IRT", symbol: "تومان", rate: 100000 },
  );
  console.assert(p.length === 2 && p[1].id === "3" && p[1].total === 790000 && Math.round(p[1].saving * 100) === 49, "buildPlans self-check failed", p);
  const all = buildPlans(
    [
      { _id: "p", name: "12", plan: "platinum", price: 7.9 },
      { _id: "promo", name: "12 promo", plan: "platinum", price: 5.9 },
      { _id: "x", name: "other", price: 2 },
      { _id: "b", name: "1", plan: "bronze", price: 1.29 },
    ],
    { code: "IRT", symbol: "تومان", rate: 100000 },
    "offline",
    true,
  );
  console.assert(
    all.map((v) => v.id).join() === "b,x,promo,p" && all[1].months === null && all.filter((v) => v.isBest).length === 1,
    "buildPlans each self-check failed",
    all,
  );
}
